<template>
    <div id="prestataires">
        <div class="app-container mb-5" v-if="!$global.loading">
            <div class="d-flex justify-content-between mt-5">

                <div class="page-title mb-3">
                    <router-link to="/campaigns">
                        <span class="breadcrumb-link">
                            <v-icon class="breadcrumb-link-icon">dashboard</v-icon> Mes campagnes
                        </span>
                    </router-link>
                    <v-icon>keyboard_arrow_right</v-icon>
                    <span class="breadcrumb-item">
                        {{ cmaj.name }}
                    </span>
                </div>

                <div class="d-flex gap-10">
                    <v-btn outlined color="primary" class="export-btn" :disabled="loadingExport" @click="prepareExport">
                        <v-icon class="mr-2" v-if="!loadingExport">arrow_downward</v-icon>
                        <v-progress-circular class="progressbar mr-2" indeterminate size="20" v-else color="primary"
                                             width="2">
                        </v-progress-circular>
                        Exporter
                        <span v-if="total">(</span>

                        <span v-if="selectAll || !selectedProviders.length">{{ total }}</span>
                        <span v-else>{{ selectedProviders.length }}</span>

                        <span v-if="total">)</span>
                    </v-btn>

                    <v-btn outlined color="primary" v-if="cmaj.apidae_selection_id === null"
                           @click="prepareAddProviderModal">
                        <v-icon class="mr-2">add</v-icon>
                        Ajouter un prestataire
                    </v-btn>

                    <v-btn :disabled="cmaj.last_batch.state.code !== 'FINISHED'" class="default-button"
                           :to="'/revival/cmaj/' + encodeUri(cmaj.id)">
                        Relancer
                    </v-btn>
                </div>
            </div>

            <div class="d-flex mt-3 mb-5">
                <div class="mt-auto mb-auto mr-5">
                    Prestataires contactés : <b>{{ cmaj.contacted_providers }}</b>
                </div>

                <div class="mt-auto mb-auto mr-5">
                    Taux de réponses : <b>{{ getRate(cmaj.answer_rate) }}</b>
                </div>

                <div class="mt-auto mb-auto mr-5">
                    Taux d'ouverture du formulaire : <b>{{ getRate(cmaj.opened_form_rate) }}</b>
                </div>

                <div class="mt-auto mb-auto mr-5">
                    Taux d'avancement : <b>{{ getRate(cmaj.complete_rate) }}</b>
                </div>

                <div class="mt-auto mb-auto" v-if="cmaj.apidae_selection !== null">
                    Sélection :
                    <a target="_blank"
                       :href="'https://base.apidae-tourisme.com/diffuser/selection/' + cmaj.apidae_selection.external_id + '?9&spid=8#tab-marquages'">
                        <b>{{ cmaj.apidae_selection.name }}</b>
                    </a>
                </div>
            </div>

            <div class="mt-2 mb-3">
                <v-card>
                    <v-card-title>
                        <filters></filters>

                        <div v-if="selectedProviders.length === options.itemsPerPage"
                             class="text-center w-100 mt-4 presta-count">

                            <div v-if="!selectAll" class="d-flex justify-content-center">
                                <div class="mt-auto mb-auto">
                                    Les <b>{{ selectedProviders.length }}</b> prestataires de cette page sont
                                    sélectionnés.
                                </div>

                                <div>
                                    <v-btn color="primary" text @click="selectAll = true">
                                        Sélectionner les {{ total }} prestataires
                                    </v-btn>
                                </div>
                            </div>

                            <div v-else class="d-flex justify-content-center">
                                <div class="mt-auto mb-auto">
                                    Les <b>{{ total }}</b> prestataires de cette recherche sont sélectionnés.
                                </div>

                                <div>
                                    <v-btn color="primary" text @click="clearSelect">
                                        Annuler la sélection
                                    </v-btn>
                                </div>
                            </div>

                        </div>
                    </v-card-title>

                    <v-data-table :headers="headers" :items="providers" :loading="loading" v-model="selectedProviders"
                                  :items-per-page="options.itemsPerPage" no-data-text="Aucun prestataires"
                                  class="elevation-1"
                                  :server-items-length="total" :options.sync="options" :sort-by.sync="sortBy"
                                  :sort-desc.sync="sortDesc" show-select :footer-props="{
                            itemsPerPageAllText: 'Tous',
                            itemsPerPageText: 'Prestataire par page',
                            itemsPerPageOptions: [10, 20, 50, 1000]
                        }">
                        <template v-slot:item.name="{ item }">
                            <div v-if="item.name === null">
                                <router-link :to="'/summary/' + encodeUri(item.id)">-</router-link>
                            </div>
                            <div v-else>
                                <router-link :to="'/summary/' + encodeUri(item.id)">{{ item.name }}</router-link>
                            </div>
                        </template>

                        <template v-slot:item.last_web_form="{ item }">
                            <div v-if="item.last_web_form.code !== null">
                                {{ item.last_web_form.label }}
                                <span v-if="item.last_web_form.token !== null && (
                                item.last_web_form.code === 'ANSWERED' ||
                                item.last_web_form.code === 'SENT_APIDAE' ||
                                item.last_web_form.code === 'SENT_TOURINSOFT' ||
                                (item.last_web_form.code === 'TREATED' && item.last_web_form.has_answer))">
                                    <v-btn color="primary" icon :to="answerUrl + item.last_web_form.token">
                                        <v-icon v-if="item.last_web_form.code === 'ANSWERED'">email</v-icon>
                                        <v-icon v-if="item.last_web_form.code === 'SENT_APIDAE' ||
                                        item.last_web_form.code === 'SENT_TOURINSOFT' ||
                                        (item.last_web_form.code === 'TREATED' && item.last_web_form.has_answer)">
                                            remove_red_eye
                                        </v-icon>
                                    </v-btn>
                                </span>
                            </div>
                        </template>

                        <template v-slot:item.actions="{ item }">
                            <v-menu bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn icon v-on="on">
                                        <v-icon>mdi-dots-vertical</v-icon>
                                    </v-btn>
                                </template>

                                <v-list>
                                    <v-list-item @click="prepareUpdateContact(item)"
                                                 :disabled="cmaj.apidae_selection !== null || item.belongs_to_selection">
                                        <v-list-item-title>
                                            <v-icon color="primary"
                                                    :disabled="cmaj.apidae_selection !== null || item.belongs_to_selection">
                                                edit
                                            </v-icon>
                                            Modifier le destinataire
                                        </v-list-item-title>
                                    </v-list-item>

                                    <v-list-item @click="resendWebForm(item)" v-if="item.last_web_form !== null &&
                                item.last_web_form.code !== 'ANSWERED' &&
                                item.last_web_form.code !== 'SENT_APIDAE' &&
                                item.last_web_form.code !== 'SENT_TOURINSOFT' &&
                                item.last_web_form.code !== 'TREATED' &&
                                (item.last_web_form.code !== 'SENT_APIDAE' ||
                                    item.last_web_form.code !== 'SENT_TOURINSOFT')">
                                        <v-list-item-title>
                                            <v-icon color="primary">send</v-icon>
                                            Renvoyer le formulaire
                                        </v-list-item-title>
                                    </v-list-item>

                                    <v-list-item v-if="item.contact !== null &&
                                    item.last_web_form.token !== null &&
                                    item.last_web_form.code !== 'SENT_APIDAE' &&
                                    item.last_web_form.code !== 'SENT_TOURINSOFT' &&
                                    item.last_web_form.code !== 'ANSWERED' &&
                                    item.last_web_form.code !== 'TREATED'" target="_blank"
                                                 :to="{ path: '/espace-pro/' + item.contact.token + '/formulaire/' + item.last_web_form.token, query: { no_redirection: 'true' } }"
                                    >
                                        <v-list-item-title>
                                            <v-icon color="primary">remove_red_eye</v-icon>
                                            Voir le formulaire
                                        </v-list-item-title>
                                    </v-list-item>

                                    <v-list-item v-if="item.last_web_form.code !== 'TREATED'"
                                                 @click="treatWebForm(item.last_web_form, item.name)">
                                        <v-list-item-title>
                                            <v-icon color="primary">done</v-icon>
                                            Marquer la réponse comme traitée
                                        </v-list-item-title>
                                    </v-list-item>

                                    <v-list-item v-if="cmaj.aspect !== null && item.last_web_form.has_answer"
                                                 @click="deleteAnswer(item.last_web_form)">
                                        <v-list-item-title>
                                            <v-icon color="primary">clear</v-icon>
                                            Refuser la réponse
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-data-table>
                </v-card>
            </div>
        </div>

        <v-dialog v-model="addProviderModal" width="700px">
            <v-card v-if="addProviderModal">
                <v-card-title>
                    <h3 class="mb-4">Ajouter un prestataire</h3>
                </v-card-title>

                <v-card-text>
                    <v-alert class="mx-auto mt-2" text>
                        Le prestataire recevra automatiquement un e-mail l'invitant à remplir ses informations.
                    </v-alert>

                    <v-alert class="mx-auto mt-2 bg-error text-white" text v-if="providerToAddEmailInvalid">
                        Ajout impossible, l'email est invalide.
                    </v-alert>

                    <v-combobox label="Prestataire" :items="providersNotInCmaj" outlined return-object item-text="name"
                                v-model="selectedProviderToAdd">
                    </v-combobox>

                    <v-text-field outlined v-if="selectedProviderToAdd !== null" label="Email destinataire"
                                  @change="checkNewEmail" v-model="selectedProviderToAdd.email">
                    </v-text-field>

                    <div class="d-flex justify-content-end gap-10">
                        <v-btn @click="addProviderModal = false">
                            Annuler
                        </v-btn>

                        <v-btn class="default-button custom-button-primary" :disabled="selectedProviderToAdd === null ||
                        selectedProviderToAdd.email === undefined ||
                        selectedProviderToAdd.email === null || providerToAddEmailInvalid" @click="addProvider">
                            Ajouter et envoyer
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <update-receiver-modale :edit-contact-modal="editContactModal" :selected-contact="selectedContact"
                                :selected-provider="selectedProvider">
        </update-receiver-modale>

        <confirm ref="confirm"></confirm>
    </div>
</template>

<script>
import Filters from "./Filters"
import _ from "lodash"
import { mapState } from "vuex"
import UpdateReceiverModale from "../../provider/UpdateReceiverModale"
import Confirm from "@/components/tools/Confirm"
import * as EmailValidator from "email-validator"

export default {
    name: "ProviderCampaign",
    components: { UpdateReceiverModale, Filters, Confirm },
    data() {
        return {
            cmajId: null,
            cmaj: {},
            providers: [],
            headers: [
                {
                    text: "Nom",
                    value: "name",
                },
                {
                    text: "Catégorie",
                    value: "category",
                },
                {
                    text: "Commune",
                    value: "commune",
                },
                {
                    text: "Destinataire",
                    value: "email",
                },
                {
                    text: "Formulaire",
                    value: "last_web_form",
                },
                {
                    text: "Actions",
                    value: "actions",
                },
            ],
            selectedProviders: [],
            total: null,
            selectAll: false,
            loadingExport: false,
            options: { itemsPerPage: 10, page: 1 },
            loading: false,
            editContactModal: false,
            selectedContact: null,
            selectedProvider: null,
            sortBy: "name",
            sortDesc: false,
            apiUrl: process.env.VUE_APP_API_URL,
            selectedProviderToAdd: null,
            providersNotInCmaj: [],
            addProviderModal: false,
            answerUrl: "/answer/",
            providerToAddEmailInvalid: false,
        }
    },
    methods: {
        getCmaj() {
            this.$global.loading = true

            this.$http.get("auth/campaigns/cmaj/" + this.cmajId).then(res => {
                this.cmaj = res.body.data.cmaj

                this.$global.loading = false
            })
        },
        prepareAddProviderModal() {
            this.selectedProviderToAdd = null

            this.getProvidersNotInCmaj()
        },
        getProvidersNotInCmaj() {
            this.$http.get("auth/campaigns/cmaj/providers/not-in-cmaj/" + this.cmaj.last_batch.id).then(res => {
                this.providersNotInCmaj = res.body.data.providers

                this.addProviderModal = true
            })
        },
        getProviders: _.debounce(function() {
            let sortBy = Array.isArray(this.sortBy) ? this.sortBy[0] : this.sortBy
            let sortDesc = Array.isArray(this.sortDesc) ? this.sortDesc[0] : this.sortDesc

            let payload = {
                perPage: this.options.itemsPerPage,
                currentPage: this.options.page,
                search: this.search,
                emailSearch: this.emailSearch,
                categories: this.selectedCategories,
                communes: this.selectedCommunes,
                tags: this.selectedTags,
                tourismSectors: this.selectedTourismSectors,
                states: this.selectedWebFormStates,
                cmajId: this.cmajId,
                sortBy: sortBy,
                sortDesc: sortDesc,
            }

            this.loading = true

            this.$http.post("auth/providers", payload).then(res => {
                this.providers = Object.values(res.body.data.providers.data)
                this.currentPage = res.body.data.providers.from
                this.total = res.body.data.providers.total

                this.selectedProviders = []

                this.importDialog = false
                this.loading = false
            })
        }, 500),
        addProvider() {
            this.$global.loading = true

            setTimeout(() => {
                let email = this.selectedProviderToAdd.email.email !== undefined ?
                  this.selectedProviderToAdd.email.email :
                  this.selectedProviderToAdd.email

                let payload = {
                    providerId: this.selectedProviderToAdd.id,
                    email: email,
                    batchId: this.cmaj.last_batch.id,
                }

                this.$http.post("auth/campaigns/cmaj/providers/add", payload).then(res => {
                    this.$global.loading = false

                    this.getProviders()

                    this.addProviderModal = false
                })

            }, 100)
        },
        slugify(str) {
            str = str.replace(/^\s+|\s+$/g, "")
            str = str.toLowerCase()

            let from = "àáäâèéëêìíïîòóöôùúüûñç·/_,:;"
            let to = "aaaaeeeeiiiioooouuuunc------"

            for (let i = 0, l = from.length; i < l; i++) {
                str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i))
            }

            return str.replace(/[^a-z0-9 -]/g, "")
              .replace(/\s+/g, "-")
              .replace(/-+/g, "-")
        },
        prepareUpdateContact(provider) {
            this.editContactModal = true
            this.selectedContact = provider.email
            this.selectedProvider = provider
        },
        encodeUri(id) {
            return btoa(id)
        },
        resendWebForm(provider) {
            let token = provider.last_web_form.token

            this.$global.loading = true

            this.$http.get("auth/webform/resend/" + token).then(res => {
                this.$global.loading = false

                this.getProviders()
            })
        },
        treatWebForm(webForm, name) {
            if (webForm.has_answer) {
                this.$global.loading = true
                this.$http.post("auth/webform/treat/" + webForm.token).then(res => {
                    this.getCmaj()
                    this.getProviders()
                    this.$global.loading = false
                })
            } else {
                this.$refs.confirm.open("Traiter la demande",
                  "Ce formulaire n'a pas reçu de réponse, êtes-vous sûr de vouloir le marquer comme \"traité\" ? " + name
                  + " ne pourra plus répondre à ce formulaire.",
                  { color: "red", backgroundTitle: "#4285F4" }).then((confirm) => {
                    if (confirm) {
                        this.$global.loading = true
                        this.$http.post("auth/webform/treat/" + webForm.token).then(res => {
                            this.getCmaj()
                            this.getProviders()
                            this.$global.loading = false
                        })
                    }
                })
            }
        },
        getRate(number) {
            if (number === null) {
                return "0%"
            }
            return Math.round(number * 100) + "%"
        },
        clearSelect() {
            this.selectedProviders = []
            this.selectAll = false
        },
        async getProviderIds(nothingMeanAll = false) {
            let providerIds = []

            if (!this.selectAll && (!nothingMeanAll || this.selectedProviders.length)) {
                providerIds = this.selectedProviders.map(provider => provider.id)
            } else {
                let payload = {
                    perPage: this.options.itemsPerPage,
                    currentPage: this.options.page,
                    search: this.search,
                    emailSearch: this.emailSearch,
                    categories: this.selectedCategories,
                    communes: this.selectedCommunes,
                    tourismSectors: this.selectedTourismSectors,
                    tags: this.selectedTags,
                    onlyIds: true,
                    cmajId: this.cmajId,
                }

                let res = await this.$http.post("auth/providers", payload)

                providerIds = res.body.data.ids

                this.$global.loading = false
            }

            return providerIds
        },
        async prepareExport() {
            this.loadingExport = true

            let payload = {
                search: this.search,
                emailSearch: this.emailSearch,
                categories: this.selectedCategories,
                communes: this.selectedCommunes,
                tags: this.selectedTags,
                tourismSectors: this.selectedTourismSectors,
                cmajId: this.cmajId,
                states: this.selectedWebFormStates,
            }

            let res = await this.$http.post("auth/campaigns/cmaj/export/generate", payload)
            let filename = res.body.data.filename

            this.loadingExport = false

            let a = document.createElement("a")

            a.href = this.apiUrl + "/export-to-xls/" + filename
            a.click()
        },
        deleteAnswer(form) {
            this.$refs.confirm.open("Etes-vous sûr de vouloir refuser cette réponse ?",
              "La réponse du prestataire sera supprimée et le formulaire sera de nouveau disponible dans l'espace pro. " +
              "Vous pourrez ensuite le renvoyer.",
              { color: "red", backgroundTitle: "#4285F4", width: 600 }).then((confirm) => {
                if (confirm) {
                    this.$global.loading = true

                    this.$http.post("auth/webform/delete-answer/" + form.token).then(res => {
                        this.$global.loading = false

                        this.getCmaj()
                        this.getProviders()
                    })
                }
            })
        },
        checkNewEmail(contact) {
            if (this.selectedProviderToAdd) {
                this.selectedProviderToAdd.email = contact
                this.providerToAddEmailInvalid = !EmailValidator.validate(this.selectedProviderToAdd.email)
            } else {
                this.providerToAddEmailInvalid = false
            }
        },
    },
    computed: {
        ...mapState("cmajFilter",
          ["search", "selectedCategories", "selectedWebFormStates", "selectedTags", "selectedCommunes",
              "selectedTourismSectors", "emailSearch"],
        ),
        ...mapState("account",
          ["user", "account"],
        ),
    },
    watch: {
        options() {
            this.getProviders()
        },
        search() {
            this.options.page = 1
            this.getProviders()
        },
        emailSearch() {
            this.options.page = 1
            this.getProviders()
        },
        selectedCategories() {
            this.options.page = 1
            this.getProviders()
        },
        selectedCommunes() {
            this.options.page = 1
            this.getProviders()
        },
        selectedTags() {
            this.options.page = 1
            this.getProviders()
        },
        selectedWebFormStates() {
            this.options.page = 1
            this.getProviders()
        },
        selectedTourismSectors() {
            this.options.page = 1
            this.getProviders()
        },
        selectedProviders() {
            if (this.selectedProviders.length !== this.options.itemsPerPage) {
                this.selectAll = false
            }
        },
        selectedProviderToAdd() {
            if (this.selectedProviderToAdd) {
                this.providerToAddEmailInvalid = !EmailValidator.validate(this.selectedProviderToAdd.email)
            } else {
                this.providerToAddEmailInvalid = false
            }
        },
    },
    created() {
        let hash = this.$route.params.hash
        this.cmajId = parseInt(atob(hash))

        if (this.account.configurations.quebec) {
            this.headers.find(header => header.text === "Commune").text = "Municipalité"
        }

        this.getCmaj()
        this.getProviders()

        this.$global.$on("load-providers", () => {
            this.$store.dispatch("account/fetchTags")

            this.getProviders()
        })

        this.$global.$on("close-update-modal", () => {
            this.editContactModal = false
        })

        if (this.account.tis_type.code === "DAHUB_QUEBEC") {
            this.answerUrl = "/sit-answer/"
        }
    },
    beforeDestroy() {
        this.$global.$off("close-update-modal")
        this.$global.$off("load-providers")
    },
}
</script>